import { inject, Service } from '@angular/core';
import { ErrorHandlingService } from '@errors/error-handling.service';
import { ConfirmationService } from '@services/confirmation';
import { MessageService } from '@services/messages';
import { GuardedActionOptions } from './guarded-action-options.model';

/**
 * Runs page actions with the shared workflow used across all pages:
 * processing guard → optional confirmation → action → success toast,
 * with centralized error handling and a guaranteed `processing` reset.
 */
@Service()
export class GuardedActionService {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly messageService = inject(MessageService);

  async runAsync(options: GuardedActionOptions): Promise<void> {
    if (options.processing()) {
      return;
    }

    if (options.confirmation && !(await this.confirmationService.confirm(options.confirmation))) {
      return;
    }

    options.processing.set(true);
    try {
      await options.action();
      if (options.successMessage) {
        this.messageService.showSuccessMessage(options.successMessage);
      }
    } catch (error) {
      options.onError?.();
      if (options.errorMessage) {
        await this.errorHandler.handleErrorAsync(error, options.errorMessage);
      }
      throw error;
    } finally {
      options.processing.set(false);
    }
  }
}
